import { useSearchParams } from 'react-router-dom'
import { useStytchB2BClient } from '@stytch/react/b2b'

import {
  Text,
  EmailLoginForm,
  Flex,
  IconGoogle,
  Link,
  GreyGrey100,
  GreyGrey70,
  GreyGrey80,
  IndigoIndigo60,
  Primary14ExtendedRegular,
  PrimaryHeader32Regular,
  type SignInOption,
  type SocialSignInOption
} from '@sequencehq/core-components'

import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { isLocalAuthBypass } from 'lib/auth/localAuth'
import { toSignInOptions } from '../ssoConnections'
import { reportAuthFailure } from 'lib/auth/reportAuthFailure'
import { backendSsoStartUrl } from '../ssoLogin'

const stageHeader = (text: string) => (
  <Text
    {...PrimaryHeader32Regular}
    textAlign="left"
    color={GreyGrey100}
    marginBottom="0px"
  >
    {text}
  </Text>
)

const stageHelper = (text: string, dataTestId?: string) => (
  <Text
    {...Primary14ExtendedRegular}
    data-testid={dataTestId}
    color={GreyGrey80}
  >
    {text}
  </Text>
)

/** With the flag off this is the bare header, exactly as it was. */
const loginHeader = (withSubheader: boolean) =>
  withSubheader ? (
    <Flex flexDirection="column" gap="8px" width="100%">
      {stageHeader('Welcome to Sequence')}
      {stageHelper(
        'Sign in to your account',
        'authUI.emailLoginForm.subheader'
      )}
    </Flex>
  ) : (
    stageHeader('Welcome to Sequence')
  )

const SSO_HEADER = stageHeader('Sign in with SSO')

export const LoginPage = () => {
  const stytch = useStytchB2BClient()
  const [searchParams] = useSearchParams()
  const { useEnterpriseSso, useSocialSignIn } = useFeatureFlags()

  const returnTo = searchParams.get('return_to') ?? '/home'
  window.localStorage.setItem('return_to', returnTo)

  // Rethrows: only a rejection tells the form the lookup was unavailable.
  const discoverSignInOptions = async (email: string) => {
    try {
      const { connections } = await stytch.sso.discoverConnections(email)
      return toSignInOptions(connections)
    } catch (error) {
      reportAuthFailure(error, 'ssoConnectionDiscovery')
      throw error
    }
  }

  // The backend start is a plain redirect, so unlike `sso.start` it cannot
  // reject: the page just leaves. In local mock-auth mode the Stytch stub's
  // start lands straight on the callback, so it stays in charge there.
  const startSso = (option: SignInOption) => {
    if (isLocalAuthBypass()) {
      return stytch.sso.start({
        connection_id: option.id,
        login_redirect_url: `${window.location.origin}/auth/callback`,
        signup_redirect_url: `${window.location.origin}/auth/callback`
      })
    }

    window.location.assign(backendSsoStartUrl(option.id))
    return Promise.resolve()
  }

  const googleSignIn: SocialSignInOption = {
    id: 'google',
    label: 'Continue with Google',
    icon: <IconGoogle />,
    onSelect: async () => {
      try {
        await stytch.oauth.google.discovery.start({
          discovery_redirect_url: `${window.location.origin}/auth/callback`
        })
      } catch (error) {
        reportAuthFailure(error, 'googleOAuthStart')
        throw error
      }
    }
  }

  const ssoStage = {
    entryLabel: 'Continue with SSO',
    email: {
      header: SSO_HEADER,
      helper: stageHelper(
        "Enter your work email and we'll take you to your identity provider."
      ),
      submitLabel: 'Continue with SSO',
      noConnectionsMessage:
        "That email isn't set up for single sign-on. Try another, or sign in with a link instead.",
      unavailableMessage:
        "We couldn't check that email just now. Try again, or sign in with a link instead.",
      magicLinkLabel: 'Sign in with a link instead'
    },
    connections: {
      header: SSO_HEADER,
      helper: stageHelper(
        'Your workspace uses single sign-on. Choose a provider below to continue.'
      ),
      magicLinkLabel: 'Continue with magic link instead',
      goBackLabel: 'Go back'
    },
    resolveOptions: discoverSignInOptions,
    onSelectOption: startSso
  }

  return (
    <EmailLoginForm
      formStage={{
        header: loginHeader(useEnterpriseSso),
        submitLabel: useEnterpriseSso ? 'Continue with email' : 'Continue',
        footer: (
          <Text
            fontSize="14px"
            color={GreyGrey70}
            textAlign="left"
            lineHeight="16px"
            width="100%"
          >
            New to Sequence?{' '}
            <Link
              color={IndigoIndigo60}
              to="https://www.sequencehq.com/book-a-demo"
            >
              Book a demo
            </Link>
          </Text>
        )
      }}
      checkEmailStage={{
        header: stageHeader('Check your email for a sign in link'),
        subjectLineFilter: 'Your login request to Sequence'
      }}
      socialOptions={useSocialSignIn ? [googleSignIn] : undefined}
      ssoStage={useEnterpriseSso ? ssoStage : undefined}
      onSubmit={async email => {
        try {
          await stytch.magicLinks.email.discovery.send({
            email_address: email,
            discovery_redirect_url: `${window.location.origin}/auth/callback`,
            login_template_id: 'magic_link_login_email'
          })
        } catch (error) {
          reportAuthFailure(error, 'magicLinkSend')
          throw error
        }
      }}
    />
  )
}

export default LoginPage
